import { ComponentChild, FunctionComponent } from 'preact';
import { useRef, useState, useCallback } from 'preact/hooks';
import cn from 'classnames';

import {
  getAssetAccess,
  getAssetImage,
  getAssetPreviewUrl,
  isSportAccessLevel,
  isSubscriptionAccessLevel,
} from '../../../../utils/player/asset';
import { VideoPreview } from '../../components/VideoPreview/VideoPreview';
import { useConfig } from '../../hooks/ConfigProvider';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { usePaywallState } from '../../hooks/usePaywallState';
import { usePlayerContainer } from '../../hooks/usePlayerContainer';
import { useViewportType } from '../../hooks/useViewportType';
import { getImageSrc } from '../../utils/image';
import { IdleLayoutElements } from '../IdleLayout/IdleLayoutElements';
import { Backdrop } from '../../components/Backdrop/Backdrop';
import { useProvider } from '../../hooks/useProvider';

import { AccessState } from './AccessState';
import { NoAccessState } from './NoAccessState';

interface PaywallLayoutProps {
  label?: string | ComponentChild;
  isAssetUpcoming?: boolean;
  isCountdownActive?: boolean;
}

export const PaywallLayout: FunctionComponent<PaywallLayoutProps> = ({
  label,
  isAssetUpcoming,
  isCountdownActive,
}) => {
  const layoutRef = useRef<HTMLDivElement>(null!);
  const { isMobile } = useViewportType();
  const { asset } = useCurrentAsset();
  const playerContainer = usePlayerContainer();

  const { configProvider } = useProvider();
  const previewUrl = getAssetPreviewUrl(asset);
  const accessLevel = getAssetAccess(asset);
  const posterSrc = getImageSrc(
    asset && getAssetImage(asset, 'main'),
    playerContainer.clientWidth
  );
  const { hasAccess } = usePaywallState(accessLevel);
  const [shouldDisplayIdleLayout, setShouldDisplayIdleLayout] = useState(
    !hasAccess &&
      !isAssetUpcoming &&
      asset &&
      isSubscriptionAccessLevel(configProvider, accessLevel)
  );

  const onPaywallLayoutClick = useCallback(() => {
    setShouldDisplayIdleLayout(false);
  }, []);

  const { context } = useConfig();

  const getContent = () => {
    if (shouldDisplayIdleLayout) {
      return <IdleLayoutElements asset={asset} />;
    }

    return (
      <Backdrop
        isVisible
        className={cn('vgp-paywall-layout-backdrop', {
          'vgp-paywall-layout-backdrop-sport': isSportAccessLevel(
            configProvider,
            accessLevel
          ),
        })}
      >
        {hasAccess ? (
          <AccessState
            context={context}
            asset={asset}
            isAssetUpcoming={isAssetUpcoming}
            isCountdownActive={isCountdownActive}
            isMobile={isMobile}
            label={label}
          />
        ) : (
          <NoAccessState
            label={label}
            asset={asset}
            isMobile={isMobile}
            context={context}
            accessLevel={accessLevel}
          />
        )}
      </Backdrop>
    );
  };

  return (
    <div
      onClick={onPaywallLayoutClick}
      ref={layoutRef}
      className="vgp-paywall-layout"
      style={{ backgroundImage: `url(${posterSrc})` }}
    >
      {previewUrl && <VideoPreview url={previewUrl} containerRef={layoutRef} />}
      {getContent()}
    </div>
  );
};
